import React from 'react';
import SkeletonBlock from '../ui/SkeletonBlock';
import SkeletonRoot from '../ui/SkeletonRoot';
import {
  adminFilterToolbarClass,
  adminListTableShellClass,
  adminListTableSkeletonHeaderClass,
  adminListTableSkeletonRowClass,
} from '../../utils/admin/adminChrome';

interface TargetInvestorsTableSkeletonProps {
  loadingLabel?: string;
  embedded?: boolean;
  /** When true, only render filter bar + table (page header/tiles already visible) */
  tableOnly?: boolean;
}

/**
 * Mirrors TargetInvestorsPage: header + metric tiles + filter bar + table.
 */
const TargetInvestorsTableSkeleton: React.FC<TargetInvestorsTableSkeletonProps> = ({
  loadingLabel = 'Loading target investors',
  embedded = false,
  tableOnly = false,
}) => {
  const tableSection = (
    <>
      <div className="mb-4 flex gap-2">
        <SkeletonBlock className="h-9 w-24 rounded-lg" />
        <SkeletonBlock className="h-9 w-28 rounded-lg" />
      </div>

      <div className={`${adminFilterToolbarClass} mb-4`}>
        <SkeletonBlock className="h-9 min-w-[200px] flex-1 max-w-sm rounded-lg" />
        <SkeletonBlock className="h-9 w-[180px] rounded-lg" />
        <SkeletonBlock className="h-9 w-[160px] rounded-lg" />
      </div>

      <div className={adminListTableShellClass}>
        <div className={adminListTableSkeletonHeaderClass}>
          <div className="flex gap-4">
            {Array.from({ length: 7 }).map((_, i) => (
              <SkeletonBlock key={i} className="h-4 flex-1" />
            ))}
          </div>
        </div>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className={adminListTableSkeletonRowClass}>
            <SkeletonBlock className="h-4 w-28" />
            <SkeletonBlock className="h-4 w-24" />
            <SkeletonBlock className="h-4 w-20" />
            <SkeletonBlock className="h-4 w-16" />
            <SkeletonBlock className="h-4 w-20" />
            <SkeletonBlock className="h-4 w-16" />
            <SkeletonBlock className="ml-auto h-6 w-16 rounded-full" />
          </div>
        ))}
      </div>
    </>
  );

  if (tableOnly) {
    return (
      <SkeletonRoot loadingLabel={loadingLabel} className="w-full">
        {tableSection}
      </SkeletonRoot>
    );
  }

  return (
    <SkeletonRoot loadingLabel={loadingLabel}>
      <div className={`${embedded ? 'min-h-0' : 'min-h-screen'} bg-white`}>
        <div className={`mx-auto w-full max-w-none px-4 sm:px-5 lg:px-6 ${embedded ? 'py-2' : 'py-8'}`}>
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <SkeletonBlock className="h-8 w-32 rounded-lg" />
            <div className="flex flex-wrap items-center gap-2">
              <SkeletonBlock className="h-9 w-9 rounded-lg" />
              <SkeletonBlock className="h-9 w-9 rounded-lg" />
              <SkeletonBlock className="h-9 w-36 rounded-xl" />
            </div>
          </div>

          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonBlock key={i} className="h-20 w-full rounded-xl" />
            ))}
          </div>

          {tableSection}
        </div>
      </div>
    </SkeletonRoot>
  );
};

export default TargetInvestorsTableSkeleton;
